import { spawnPointOf, type ArenaBox, type ArenaDefinition } from "./arena.ts";
import type { FighterKit } from "./character.ts";
import { Action, Combat as C, Press, actionLength } from "./combat.ts";
import { AXIS_MAX, Button, type InputCommand } from "./input.ts";
import { Movement as M } from "./movement.ts";

/**
 * Everything the movement simulation reads and writes for one fighter.
 *
 * The same `stepPlayer` runs on the server (authority) and on the client
 * (prediction + rollback replay), so it must be deterministic:
 * - only + - * / and Math.sqrt (correctly rounded on every engine), no trig
 *   or Math.exp, whose results may differ between V8 and JavaScriptCore;
 * - floats are rounded to float32 at the end of each step, which is exactly
 *   what the wire carries, so the client replays from bit-identical state.
 */
export interface PlayerSim {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Facing direction on the XZ plane (unit vector). */
  fx: number;
  fz: number;
  grounded: boolean;
  /** Ticks spent airborne; drives coyote time. Saturates at 255. */
  airTicks: number;
  dashTicks: number;
  dashCooldown: number;
  spawnIndex: number;
  /** Current `Action` (attack or Super Slam); `Action.None` when free. */
  action: number;
  /** Steps since the action started: 0 on the step it starts. */
  actionTick: number;
  /** Stable action identity for deduplicating predicted visual effects after rollback. */
  actionSerial: number;
  /** Attack press (`Press`) waiting until the fighter can act, for `queueTicks` more steps. */
  queued: number;
  queueTicks: number;
  /** Steps left without control after a hit. Set by the server, run down here. */
  hitstun: number;
  /** Full Slam meter during a match: granted by the server, spent here when the Super Slam starts. */
  slamReady: boolean;
  /** Steps before S1 / S2 can be used again. */
  cooldown1: number;
  cooldown2: number;
  /** Kind of the object carried (D39), 0 = hands free. Set by the server, cleared here by the throw. */
  carrying: number;
  slowTicks: number;
  slipTicks:number;
  /** Maria Antonietta's whirlwind: faster, steered by the stick, no other move. Set by the server, run down here. */
  spinTicks: number;
  /** Virgilio: fixed horizontal position, no inputs; airborne victims fall rapidly. */
  anchorTicks:number;
  airBounceUsed: boolean;
  countered: boolean;
  /** Server controls the respawn timer; prediction stays frozen until released. */
  knockedOut: boolean;
  matchFrozen: boolean;
  /** Controlled by the authoritative short grab, never by input. */
  grabbed: boolean;
}

/** Fields the client reconciler mirrors from the server (see PlayerState). */
export const SIM_FIELDS = [
  "x", "y", "z", "vx", "vy", "vz", "fx", "fz",
  "grounded", "airTicks", "dashTicks", "dashCooldown", "spawnIndex",
  "action", "actionTick", "actionSerial", "queued", "queueTicks", "hitstun", "slamReady", "cooldown1", "cooldown2", "carrying", "slowTicks", "slipTicks", "spinTicks", "anchorTicks", "airBounceUsed", "countered", "knockedOut", "matchFrozen", "grabbed",
] as const satisfies readonly (keyof PlayerSim)[];

const EPSILON = 1e-4;
/** Fraction of the radius that must overlap a top face to stand on it. */
const SUPPORT_RADIUS_FACTOR = 0.6;

export function createPlayerSim(arena: ArenaDefinition, spawnIndex: number): PlayerSim {
  const sim: PlayerSim = {
    x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, fx: 0, fz: 1,
    grounded: false, airTicks: 0, dashTicks: 0, dashCooldown: 0, spawnIndex,
    action: Action.None, actionTick: 0, actionSerial: 0, queued: Press.None, queueTicks: 0, hitstun: 0, slamReady: false,
    cooldown1: 0, cooldown2: 0, carrying: 0, slowTicks: 0, slipTicks:0, spinTicks: 0, anchorTicks:0, airBounceUsed: false, countered: false, knockedOut: false, matchFrozen: false, grabbed: false,
  };
  placeAtSpawn(sim, arena);
  return sim;
}

/** Puts the fighter on its spawn point, facing the arena centre, with no action or stun in progress. */
export function placeAtSpawn(s: PlayerSim, arena: ArenaDefinition): void {
  const spawn = spawnPointOf(arena, s.spawnIndex);
  if (!spawn) throw new Error(`Arena ${arena.id} has no spawn points`);
  s.x = spawn.x;
  s.y = spawn.y;
  s.z = spawn.z;
  s.vx = 0;
  s.vy = 0;
  s.vz = 0;
  const len = Math.sqrt(spawn.x * spawn.x + spawn.z * spawn.z);
  if (len > EPSILON) {
    s.fx = -spawn.x / len;
    s.fz = -spawn.z / len;
  } else {
    s.fx = 0;
    s.fz = 1;
  }
  s.grounded = true;
  s.airTicks = 0;
  s.dashTicks = 0;
  s.dashCooldown = 0;
  s.action = Action.None;
  s.actionTick = 0;
  s.queued = Press.None;
  s.queueTicks = 0;
  s.hitstun = 0;
  s.cooldown1 = 0;
  s.cooldown2 = 0;
  s.carrying = 0;
  s.slowTicks = 0;
  s.slipTicks=0;
  s.spinTicks = 0;
  s.anchorTicks=0;
  s.airBounceUsed = false;
  s.countered = false;
  s.knockedOut = false;
  s.matchFrozen = false;
  s.grabbed = false;
  quantize(s);
}

/**
 * Advances one fighter by one fixed step. Mutates `s` in place.
 *
 * The combat state machine lives here too, so the client predicts its own
 * attacks exactly: free → (attack startup → active → recovery, with chains
 * into the next attack) → free, plus dash, ability cooldowns and hitstun.
 * Only the server puts a fighter in hitstun or grants `slamReady`; this
 * function just runs them down. `kit` is the fighter's champion: its moves
 * and its movement numbers.
 */
const ghostWorlds = new WeakMap<ArenaDefinition, ArenaDefinition>();

/** What a ghost bumps into (D49): the walls and floors, not the furniture it floats through. */
function ghostWorld(arena: ArenaDefinition): ArenaDefinition {
  let world = ghostWorlds.get(arena);
  if (!world) {
    world = { ...arena, solids: arena.solids.filter((b) => !b.passable) };
    ghostWorlds.set(arena, world);
  }
  return world;
}

export function stepPlayer(s: PlayerSim, cmd: InputCommand, arena: ArenaDefinition, dt: number, kit: FighterKit): void {
  if (kit.ghost) arena = ghostWorld(arena);
  if (s.knockedOut || s.matchFrozen || s.grabbed) return;
  if(s.anchorTicks>0){
    s.anchorTicks--;s.vx=s.vz=0;s.dashTicks=0;
    s.action=Action.None;s.actionTick=0;s.queued=Press.None;s.queueTicks=0;
    s.hitstun=Math.max(0,s.hitstun-1);
    if(s.cooldown1>0)s.cooldown1--;if(s.cooldown2>0)s.cooldown2--;
    if(s.dashCooldown>0)s.dashCooldown--;if(s.slowTicks>0)s.slowTicks--;if(s.slipTicks>0)s.slipTicks--;if(s.spinTicks>0)s.spinTicks--;
    if(!s.grounded)s.vy=Math.min(s.vy,-18);else s.vy=0;
    applyGravity(s,dt);moveAndCollide(s,arena,dt);
    if(s.y<arena.killY)placeAtSpawn(s,arena);
    quantize(s);return;
  }
  let ix = cmd.mx / AXIS_MAX;
  let iz = cmd.mz / AXIS_MAX;
  let mag = Math.sqrt(ix * ix + iz * iz);
  if (mag > 1) {
    ix /= mag;
    iz /= mag;
    mag = 1;
  }
  const moving = mag > M.deadzone;
  if (!moving) {
    ix = 0;
    iz = 0;
  }

  // Timers. `stunned` covers this whole step, so hitstun N = N steps without control.
  if (s.dashCooldown > 0) s.dashCooldown -= 1;
  if (s.cooldown1 > 0) s.cooldown1 -= 1;
  if (s.cooldown2 > 0) s.cooldown2 -= 1;
  if (s.slowTicks > 0) s.slowTicks -= 1;
  if(s.slipTicks>0)s.slipTicks--;
  // Spinning, the stick steers and every button waits: no attack, jump or dash.
  const spinning = s.spinTicks > 0;
  if (spinning) {
    s.spinTicks -= 1;
    s.queued = Press.None;
    s.queueTicks = 0;
  }
  // A ghost (no champion yet, D49) only walks and jumps.
  const buttons = spinning ? 0 : kit.ghost ? cmd.buttons & Button.Jump : cmd.buttons;
  if (s.queueTicks > 0) {
    s.queueTicks -= 1;
    if (s.queueTicks === 0) s.queued = Press.None;
  }
  const stunned = s.hitstun > 0;
  if (stunned) s.hitstun -= 1;
  if (s.action !== Action.None) advanceAction(s, kit);
  // The object leaves the hands on the throw's live step (the server launches it then).
  if (s.action === Action.Throw && s.actionTick === kit.table[Action.Throw]!.startup) s.carrying = 0;

  // Attack presses wait in a short buffer: a tap during an attack chains into
  // the next one as soon as the attack allows it, a tap during a dash fires after it.
  const press = pressedAttack(buttons, s.slamReady);
  if (press !== Press.None) {
    s.queued = press;
    s.queueTicks = C.pressBufferSteps;
  }

  let actionStarted = false;
  // Jump is resolved before attack selection, including simultaneous mobile presses.
  if (!stunned && s.action === Action.None && s.dashTicks === 0 &&
      (buttons & Button.Jump) !== 0 && (s.grounded || s.airTicks <= M.coyoteTicks)) {
    s.vy = kit.jumpSpeed;
    s.grounded = false;
    s.airTicks = M.coyoteTicks + 1;
  }
  if (!stunned && s.dashTicks > 0 && s.queued === Press.Light && s.carrying === 0 && kit.moves.dashAttack) {
    s.dashTicks = 0;
    actionStarted = tryStartAction(s, kit, moving ? ix / mag : 0, moving ? iz / mag : 0, true);
  }
  if (!actionStarted && !stunned && s.dashTicks === 0) {
    const free = s.action === Action.None;
    if (free && moving) {
      s.fx = ix / mag;
      s.fz = iz / mag;
    }
    if (free && (buttons & Button.Dash) !== 0 && s.dashCooldown === 0 && s.carrying === 0) {
      s.dashTicks = M.dashTicks;
      s.dashCooldown = M.dashCooldownTicks;
      s.vx = s.fx * kit.dashSpeed;
      s.vz = s.fz * kit.dashSpeed;
      s.vy = 0;
    } else {
      actionStarted = tryStartAction(s, kit, moving ? ix / mag : 0, moving ? iz / mag : 0);
    }
  }

  let dashEnded = false;
  if (s.dashTicks > 0) {
    // Dashing: fixed horizontal velocity, no gravity.
    s.dashTicks -= 1;
    dashEnded = s.dashTicks === 0;
  } else if (stunned) {
    // Knockback slides out; no control.
    approachVelocity(s, 0, 0, (s.grounded ? C.knockbackFriction : M.airDecel) * dt);
    applyGravity(s, dt);
  } else if (s.action !== Action.None) {
    const move = kit.table[s.action];
    if (move?.landingAttack && !s.grounded && s.actionTick >= move.startup - 1) s.vy = Math.min(s.vy, -20);
    if (move && move.airBounce>0 && !s.grounded && s.actionTick>=move.startup && s.actionTick<move.startup+move.active) s.vy=Math.min(s.vy,-7);
    if (move && move.travel > 0 && s.actionTick < move.startup + move.active) {
      // Rushes and dash strikes carry the fighter forward until the hit is over.
      s.vx = s.fx * move.travel;
      s.vz = s.fz * move.travel;
    } else if (!actionStarted) {
      // Attacks plant the fighter (after the lunge of their first step); momentum carries in the air.
      approachVelocity(s, 0, 0, (s.grounded ? C.actionDecel : M.airDecel) * dt);
    }
    applyGravity(s, dt);
  } else {
    const accel = s.grounded
      ? (moving ? M.groundAccel : M.groundDecel)
      : (moving ? M.airAccel : M.airDecel);
    const top = kit.maxSpeed * (s.carrying !== 0 ? C.carrySpeed : 1) * (s.slowTicks > 0 ? C.saltSpeed : 1) * (spinning ? C.spinSpeed : 1);
    approachVelocity(s, ix * top, iz * top, accel * (s.grounded && s.slipTicks>0 ? .12 : 1) * dt);

    applyGravity(s, dt);
  }

  const airborneDive = !s.grounded && kit.table[s.action]?.landingAttack;
  moveAndCollide(s, arena, dt);
  if (s.grounded) s.airBounceUsed=false;
  if (airborneDive && s.grounded) s.actionTick = kit.table[s.action]!.startup;
  if (dashEnded) clampHorizontalSpeed(s, kit.maxSpeed);

  if (s.y < arena.killY) {
    placeAtSpawn(s, arena);
    return;
  }
  quantize(s);
}

/** Pushes a fighter out of the arena's solids (after the server moved it, e.g. body collisions). */
export function resolveArenaOverlap(s: PlayerSim, arena: ArenaDefinition): void {
  for (const box of arena.solids) pushOutHorizontally(s, box);
  quantize(s);
}

/** Highest-priority button pressed this step: ★ (only with a full meter), then S1, S2, B, A. */
function pressedAttack(buttons: number, slamReady: boolean): number {
  if ((buttons & Button.Slam) !== 0 && slamReady) return Press.Slam;
  if ((buttons & Button.Special1) !== 0) return Press.Special1;
  if ((buttons & Button.Special2) !== 0) return Press.Special2;
  if ((buttons & Button.Grab) !== 0) return Press.Grab;
  if ((buttons & Button.Light) !== 0) return Press.Light;
  return Press.None;
}

function advanceAction(s: PlayerSim, kit: FighterKit): void {
  const def = kit.table[s.action];
  if (def?.landingAttack && !s.grounded && s.actionTick >= def.startup - 1) return;
  s.actionTick += 1;
  if (!def || s.actionTick >= actionLength(def)) {
    s.action = Action.None;
    s.actionTick = 0;
    s.countered = false;
  }
}

/**
 * Starts the buffered attack if the fighter can: from free, or as a chain
 * once the current attack reaches `cancelFrom`. An ability waits for its
 * cooldown (while the press stays buffered). `(aimX, aimZ)` is the stick
 * direction (zero when centred): chains can re-aim.
 */
function tryStartAction(s: PlayerSim, kit: FighterKit, aimX: number, aimZ: number, dashAttack = false): boolean {
  if (s.queued === Press.None) return false;
  const current = kit.table[s.action];
  if (s.carrying !== 0 || s.queued === Press.Grab) {
    // Hands full: A or B throws, nothing else. Hands free: B is the server's (it picks up).
    if (s.carrying === 0 || current) {
      if (s.carrying === 0) {
        s.queued = Press.None;
        s.queueTicks = 0;
      }
      return false;
    }
    if (s.queued !== Press.Light && s.queued !== Press.Grab) {
      s.queued = Press.None;
      s.queueTicks = 0;
      return false;
    }
  }
  const special = s.queued === Press.Special1
    ? (s.cooldown1 === 0 ? Action.Special1 : Action.None)
    : s.queued === Press.Special2
      ? (s.cooldown2 === 0 ? Action.Special2 : Action.None)
      : Action.None;
  let next: number = Action.None;
  if (!current) {
    if (s.carrying !== 0) next = Action.Throw;
    else if (s.queued === Press.Light) next = dashAttack ? Action.DashAttack : (!s.grounded && kit.moves.airAttack && !(kit.moves.airAttack.airBounce>0 && s.airBounceUsed) ? Action.AirAttack : Action.Light1);
    else if (s.queued === Press.Slam) next = s.slamReady ? Action.SuperSlam : Action.None;
    else next = special;
  } else if (s.actionTick >= current.cancelFrom) {
    if (s.queued === Press.Light) next = current.nextLight;
    else if (s.queued === Press.Slam) next = s.slamReady && current.superCancel ? Action.SuperSlam : Action.None;
    else if (current.specialCancel) next = special;
  }
  const def = kit.table[next];
  if (!def) return false; // keep it buffered: it may still chain later
  if ((def.counterUntil>0 || def.groundOnly) && !s.grounded) return false;

  s.queued = Press.None;
  s.queueTicks = 0;
  if (aimX !== 0 || aimZ !== 0) {
    s.fx = aimX;
    s.fz = aimZ;
  }
  s.action = next;
  s.actionSerial = (s.actionSerial + 1) & 65535;
  s.actionTick = 0;
  s.countered = false;
  if (next === Action.SuperSlam) s.slamReady = false;
  else if (next === Action.Special1) s.cooldown1 = def.cooldown;
  else if (next === Action.Special2) s.cooldown2 = def.cooldown;
  if (s.grounded) {
    s.vx = s.fx * def.lunge;
    s.vz = s.fz * def.lunge;
  }
  return true;
}

function applyGravity(s: PlayerSim, dt: number): void {
  if (s.grounded) return;
  const gravity = s.vy < 0 ? M.gravity * M.fallGravityMultiplier : M.gravity;
  s.vy = Math.max(s.vy - gravity * dt, -M.maxFallSpeed);
}

function approachVelocity(s: PlayerSim, targetX: number, targetZ: number, maxDelta: number): void {
  const dx = targetX - s.vx;
  const dz = targetZ - s.vz;
  const dist = Math.sqrt(dx * dx + dz * dz);
  if (dist <= maxDelta || dist < EPSILON) {
    s.vx = targetX;
    s.vz = targetZ;
  } else {
    s.vx += (dx / dist) * maxDelta;
    s.vz += (dz / dist) * maxDelta;
  }
}

function clampHorizontalSpeed(s: PlayerSim, max: number): void {
  const speed = Math.sqrt(s.vx * s.vx + s.vz * s.vz);
  if (speed > max) {
    s.vx = (s.vx / speed) * max;
    s.vz = (s.vz / speed) * max;
  }
}

function moveAndCollide(s: PlayerSim, arena: ArenaDefinition, dt: number): void {
  // Horizontal: sub-step so a dash cannot tunnel through thin geometry.
  const dx = s.vx * dt;
  const dz = s.vz * dt;
  const dist = Math.sqrt(dx * dx + dz * dz);
  const steps = Math.max(1, Math.ceil(dist / (M.radius * 0.5)));
  for (let i = 0; i < steps; i++) {
    s.x += dx / steps;
    s.z += dz / steps;
    for (const box of arena.solids) pushOutHorizontally(s, box);
  }

  // Vertical: swept against top and bottom faces, so no tunnelling either.
  const prevY = s.y;
  s.y += s.vy * dt;
  const supportRadius = M.radius * SUPPORT_RADIUS_FACTOR;
  let landingTop: number | null = null;
  let ceiling: number | null = null;
  for (const box of arena.solids) {
    if (!circleOverlapsBox(s.x, s.z, supportRadius, box)) continue;
    // Tolerance above the top too: positions are float32, so standing on a top that float32
    // cannot represent (1.2 → 1.2000000477) must still count as standing on it, or the
    // fighter would flicker between grounded and airborne at every step.
    if (s.vy <= 0 && prevY >= box.max.y - EPSILON && s.y <= box.max.y + EPSILON) {
      if (landingTop === null || box.max.y > landingTop) landingTop = box.max.y;
    } else if (s.vy > 0 && prevY + M.height <= box.min.y + EPSILON && s.y + M.height > box.min.y) {
      if (ceiling === null || box.min.y < ceiling) ceiling = box.min.y;
    }
  }

  if (landingTop !== null) {
    s.y = landingTop;
    s.vy = 0;
    s.grounded = true;
  } else {
    if (ceiling !== null) {
      s.y = ceiling - M.height;
      s.vy = 0;
    }
    s.grounded = false;
  }
  s.airTicks = s.grounded ? 0 : Math.min(255, s.airTicks + 1);
}

/** Resolves overlap between the fighter cylinder and one box on the XZ plane. */
function pushOutHorizontally(s: PlayerSim, box: ArenaBox): void {
  const overlapsVertically = s.y < box.max.y - EPSILON && s.y + M.height > box.min.y + EPSILON;
  if (!overlapsVertically) return;

  const cx = clamp(s.x, box.min.x, box.max.x);
  const cz = clamp(s.z, box.min.z, box.max.z);
  let nx = s.x - cx;
  let nz = s.z - cz;
  const d2 = nx * nx + nz * nz;
  if (d2 >= M.radius * M.radius) return;

  if (d2 > 1e-12) {
    const d = Math.sqrt(d2);
    nx /= d;
    nz /= d;
    const push = M.radius - d;
    s.x += nx * push;
    s.z += nz * push;
  } else {
    // Centre inside the box: leave through the nearest side.
    const toMinX = s.x - box.min.x;
    const toMaxX = box.max.x - s.x;
    const toMinZ = s.z - box.min.z;
    const toMaxZ = box.max.z - s.z;
    const nearest = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
    nx = 0;
    nz = 0;
    if (nearest === toMinX) { s.x = box.min.x - M.radius; nx = -1; }
    else if (nearest === toMaxX) { s.x = box.max.x + M.radius; nx = 1; }
    else if (nearest === toMinZ) { s.z = box.min.z - M.radius; nz = -1; }
    else { s.z = box.max.z + M.radius; nz = 1; }
  }

  // Slide: remove the velocity component pointing into the box.
  const into = s.vx * nx + s.vz * nz;
  if (into < 0) {
    s.vx -= into * nx;
    s.vz -= into * nz;
  }
}

function circleOverlapsBox(x: number, z: number, r: number, box: ArenaBox): boolean {
  const dx = x - clamp(x, box.min.x, box.max.x);
  const dz = z - clamp(z, box.min.z, box.max.z);
  return dx * dx + dz * dz < r * r;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function quantize(s: PlayerSim): void {
  s.x = Math.fround(s.x);
  s.y = Math.fround(s.y);
  s.z = Math.fround(s.z);
  s.vx = Math.fround(s.vx);
  s.vy = Math.fround(s.vy);
  s.vz = Math.fround(s.vz);
  s.fx = Math.fround(s.fx);
  s.fz = Math.fround(s.fz);
}
